import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import { FiArrowLeft } from 'react-icons/fi';

const Arrow = FiArrowLeft as unknown as React.FC<React.SVGProps<SVGSVGElement>>;

const SignInContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-primary);
  padding: 20px;
  position: relative;
`;

const SignInCard = styled.div`
  padding: 3rem;
  width: 100%;
  max-width: 450px;
  text-align: center;
`;

const Title = styled.h1`
  color: var(--accent-orange);
  margin-bottom: 2rem;
  font-size: 2.5rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 2px;
  font-family: 'Poppins', sans-serif;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  text-align: left;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: var(--text-gray);
  font-weight: 700;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-family: 'Poppins', sans-serif;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid #e0e6ed;
  border-radius: var(--radius-md);
  font-size: 1rem;
  background: #f5f5f5;
  color: var(--text-gray);
  transition: var(--transition-normal);
  box-sizing: border-box;
  font-family: 'Poppins', sans-serif;

  &:focus {
    outline: none;
    border-color: var(--accent-orange);
    background: var(--bg-primary);
    box-shadow: 0 0 0 3px rgba(255, 150, 0, 0.2);
  }

  &::placeholder {
    color: var(--text-muted);
  }
`;

const Button = styled.button`
  padding: 0.4rem 1rem;
  border-radius: 0.5rem;
  border: 2px solid var(--accent-orange);
  background: var(--accent-orange);
  color: var(--text-light);
  font-size: 0.75rem;
  font-weight: 800;
  font-family: 'Poppins', sans-serif;
  text-transform: uppercase;
  letter-spacing: -0.02em;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 1.5rem;
  min-width: 120px;
  white-space: nowrap;

  &:hover {
    background: var(--accent-orange-dark);
    border-color: var(--accent-orange-dark);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const ErrorMessage = styled.div`
  color: #ff6b6b;
  background: rgba(255, 107, 107, 0.1);
  border: 1px solid rgba(255, 107, 107, 0.3);
  border-radius: var(--radius-md);
  padding: 1rem;
  margin-bottom: 1.5rem;
  font-size: 0.9rem;
  font-weight: 500;
`;

const LinkText = styled.p`
  margin-top: 2rem;
  color: var(--text-gray);
  font-size: 0.9rem;
  font-family: 'Poppins', sans-serif;

  a {
    color: var(--accent-orange);
    text-decoration: none;
    font-weight: 600;
    transition: var(--transition-normal);

    &:hover {
      color: var(--accent-orange-dark);
      text-decoration: underline;
    }
  }
`;

const GoogleButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  padding: var(--button-padding, 0.4rem 0.75rem);
  border: 2px solid #e0e6ed;
  border-radius: var(--radius-md);
  font-size: var(--button-font-size, 0.6rem);
  font-weight: 600;
  font-family: 'Poppins', sans-serif;
  background: var(--bg-primary);
  color: var(--text-gray);
  cursor: pointer;
  transition: var(--transition-normal);
  margin-bottom: 1.5rem;
  white-space: nowrap;

  &:hover {
    border-color: var(--accent-orange);
    background: rgba(255, 150, 0, 0.05);
    transform: translateY(-2px);
    box-shadow: var(--shadow-md);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  margin: 1.5rem 0;
  text-align: center;
  
  &::before,
  &::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid var(--card-border);
  }
  
  span {
    padding: 0 1rem;
    color: var(--text-gray);
    font-size: 0.9rem;
    font-family: 'Poppins', sans-serif;
  }
`;

const BackButton = styled.button`
  position: absolute;
  top: 2rem;
  left: 2rem;
  padding: 0.5rem;
  font-size: 1.2rem;
  border: 2px solid var(--accent-orange);
  border-radius: 50%;
  cursor: pointer;
  background: transparent;
  color: var(--accent-orange);
  transition: var(--transition-normal);
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;

  &:hover {
    background: var(--accent-orange);
    color: var(--text-light);
    transform: translateY(-1px);
    box-shadow: var(--shadow-md);
  }

  &:active {
    transform: translateY(0);
  }
`;

const SignIn: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [formError, setFormError] = useState('');
  const { login, loginWithGoogle, isLoading, error } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Get return URL from query params
  const returnUrl = searchParams.get('returnUrl');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    if (!email || !password) {
      setFormError('Please fill in all fields');
      return;
    }

    try {
      const user = await login(email, password);
      // Redirect to return URL if provided, otherwise based on user role
      if (returnUrl) {
        navigate(returnUrl);
      } else if (user.role === 'coach') {
        navigate('/coach/home');
      } else {
        navigate('/jumper');
      }
    } catch (error) {
      // Error is already handled by the auth context
    }
  };

  const handleGoogleSignIn = () => {
    loginWithGoogle();
  };

  const handleBack = () => {
    navigate('/');
  };

  return (
    <SignInContainer>
      <BackButton onClick={handleBack}>
        <Arrow />
      </BackButton>
      <SignInCard>
        <Title>Sign In</Title>
        
        {(error || formError) && (
          <ErrorMessage>
            {formError || error}
          </ErrorMessage>
        )}

        <GoogleButton onClick={handleGoogleSignIn} disabled={isLoading}>
          <svg width="20" height="20" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google
        </GoogleButton>

        <Divider>
          <span>OR</span>
        </Divider>

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </FormGroup>

          <Button type="submit" disabled={isLoading}>
            {isLoading ? 'Signing In...' : 'Sign In'}
          </Button>
        </Form>

        <LinkText>
          Don't have an account? <Link to="/signup">Sign up for free.</Link>
        </LinkText>
      </SignInCard>
    </SignInContainer>
  );
};

export default SignIn; 